import React from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';

const Home = () => {
  const verticals = [
    {
      name: 'Solace Kids & CAP',
      description: 'For Child and adolescent mental health, developmental, and behavioral support',
      href: 'https://solacekids.in/',
      tone: 'from-amber-200/70 via-amber-100 to-white',
      accent: 'text-amber-700'
    },
    {
      name: 'Solace Senior Care',
      description: 'Specialized psychiatric care and wellbeing for older adults',
      href: 'https://solaceseniorcare.com',
      tone: 'from-emerald-200/70 via-emerald-100 to-white',
      accent: 'text-emerald-700'
    },
    {
      name: 'My Solace',
      description: 'Digital-first support with flexible access to care and resources.',
      href: 'https://mysolace.online',
      tone: 'from-sky-200/70 via-sky-100 to-white',
      accent: 'text-sky-700'
    },
    {
      name: 'Solace for Adults',
      description: 'Mental health care support system for adults with premium and budget facilities',
      tone: 'from-violet-200/70 via-violet-100 to-white',
      accent: 'text-violet-700'
    }
  ];

  const highlights = [
    {
      title: 'Integrated care pathways',
      copy: 'Coordinated clinical teams and evidence-based treatment plans across every stage.'
    },
    {
      title: 'Compassion-led innovation',
      copy: 'Modern facilities paired with humane, patient-centered psychiatry.'
    },
    {
      title: 'Community-first outreach',
      copy: 'Programs that make mental healthcare more accessible to families and caregivers.'
    }
  ];

  const latestPosts = [...blogPosts]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 4);
  const [featuredPost, ...secondaryPosts] = latestPosts;

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Hero */}
        <section className="relative overflow-hidden bg-[#00A785] text-white w-full">
        <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-white/10 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-white/10 blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid gap-14 lg:grid-cols-[1.05fr_0.95fr] items-center">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.3em] text-white/70 mb-5">
                Solace Neuro
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-tight">
                Building a complete continuum of mental healthcare across Kerala.
              </h1>
              <p className="mt-6 text-lg text-white/80">
                Bringing together specialist teams, modern facilities, and compassionate care for children,
                adults, and seniors.
              </p>
              <div className="mt-8">
                <Link
                  to="/about"
                  className="btn bg-white text-primary-700 hover:bg-gray-100 px-6 py-3 text-base font-semibold"
                >
                  View further information
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-[48px] rounded-bl-[220px] bg-white p-4 shadow-2xl">
                <div className="rounded-[36px] overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1576765607924-3f7b8410a787?auto=format&fit=crop&w=1400&q=80"
                    alt="Solace Neuro care team"
                    className="w-full h-[360px] md:h-[420px] object-cover"
                  />
                </div>
              </div>
              <div className="absolute -bottom-6 left-6 h-28 w-28 rounded-full bg-[#00A785]/60 blur-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Groups */}
      <section className="py-16 bg-gradient-to-br from-primary-50 via-white to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center gap-4 mb-10">
            <div>
              <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest">Our Verticals</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 max-w-3xl">
                Care that grows with every life stage
              </h2>
            </div>
            <p className="text-gray-600 max-w-2xl">
              Each vertical delivers specialized expertise while staying connected through the Solace network.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {verticals.map((vertical) => {
              const CardWrapper = vertical.href ? 'a' : 'div';
              const wrapperProps = vertical.href
                ? { href: vertical.href, target: '_blank', rel: 'noreferrer' }
                : {};
              return (
                <CardWrapper
                  key={vertical.name}
                  {...wrapperProps}
                  className="group rounded-3xl border border-white/60 bg-white shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden flex flex-col"
                >
                  <div className={`h-32 bg-gradient-to-br ${vertical.tone} flex items-center justify-center text-center px-6`}>
                    <span className={`text-xl font-semibold ${vertical.accent}`}>{vertical.name}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1 text-center">
                    <p className="text-sm text-gray-600">{vertical.description}</p>
                    <div className="mt-5 text-sm font-semibold text-primary-600 group-hover:text-primary-700 inline-flex items-center justify-center gap-2">
                      Visit website <span aria-hidden="true">→</span>
                    </div>
                  </div>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </section>


      {/* Highlights */}
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Centered heading */}
        <div className="flex flex-col items-center text-center mb-14">
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-primary-600 mb-3">
            Why Solace
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold text-[#042C53] leading-snug max-w-xl">
            Experience the difference in integrated mental healthcare
          </h2>
          <p className="mt-4 text-[15px] text-gray-400 max-w-md leading-relaxed">
            We combine clinical depth, thoughtful spaces, and continuous support for patients and families.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid md:grid-cols-3 gap-[2px] bg-primary-100 rounded-[20px] overflow-hidden">
          {highlights.map((highlight, i) => (
            <div
              key={highlight.title}
              className="bg-white px-8 py-10 flex flex-col"
            >
              <p className="text-[11px] font-medium tracking-[0.15em] text-primary-200 mb-7">
                {String(i + 1).padStart(2, '0')}
              </p>
              <h3 className="text-[17px] font-semibold text-[#042C53] leading-snug mb-2.5">
                {highlight.title}
              </h3>
              <p className="text-[13px] text-[#6b8caa] leading-relaxed">
                {highlight.copy}
              </p>
            </div>
          ))}
        </div>

        {/* Dark CTA strip */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 bg-[#042C53] rounded-2xl px-9 py-7">
          <p className="text-[18px] font-semibold text-white leading-snug max-w-sm">
            Ready to find the right care
            for you or your family?
          </p>
          <Link
            to="/services/adult"
            className="flex-shrink-0 text-[13px] font-semibold text-[#042C53] bg-white hover:bg-primary-50 px-6 py-3 rounded-full inline-flex items-center gap-2 transition-colors"
          >
            View all services →
          </Link>
        </div>

      </div>
    </section>

      {/* Featured Posts */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center gap-4 mb-10">
            <div>
              <p className="text-sm font-semibold text-primary-600 uppercase tracking-widest">Featured</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">
                Latest from our blog
              </h2>
            </div>
            <Link
              to="/blog"
              className="text-sm font-semibold text-primary-600 hover:text-primary-700 inline-flex items-center gap-2"
            >
              View all articles
              <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-6">
            {featuredPost ? (
              <article className="group rounded-[2rem] overflow-hidden border border-gray-100 bg-white shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="overflow-hidden">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-72 md:h-80 object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="p-8">
                  <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-primary-600">
                    <span>{featuredPost.category}</span>
                    <span className="text-gray-300">•</span>
                    <span className="tracking-normal normal-case text-gray-500">{featuredPost.date}</span>
                  </div>
                  <h3 className="mt-4 text-2xl md:text-3xl font-semibold text-gray-900 leading-tight">
                    {featuredPost.title}
                  </h3>
                  <p className="mt-4 text-base text-gray-600 max-w-2xl">
                    {featuredPost.excerpt}
                  </p>
                  <div className="mt-6 flex items-center justify-between">
                    <span className="text-sm text-gray-500">{featuredPost.readTime}</span>
                    <Link
                      to={`/blog?post=${featuredPost.id}`}
                      className="text-sm font-semibold text-primary-600 group-hover:text-primary-700"
                    >
                      Read article →
                    </Link>
                  </div>
                </div>
              </article>
            ) : null}
            <div className="space-y-4">
              {secondaryPosts.map((post) => (
                <article
                  key={post.id}
                  className="group rounded-3xl border border-gray-100 bg-white p-5 shadow-sm hover:shadow-md transition-all duration-300"
                >
                  <div className="flex gap-4">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="h-24 w-24 rounded-2xl object-cover flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
                        <span>{post.category}</span>
                        <span className="text-gray-300">•</span>
                        <span className="tracking-normal normal-case text-gray-500">{post.date}</span>
                      </div>
                      <h4 className="mt-2 text-lg font-semibold text-gray-900 leading-snug">
                        {post.title}
                      </h4>
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <span className="text-sm text-gray-500">{post.readTime}</span>
                        <Link
                          to={`/blog?post=${post.id}`}
                          className="text-sm font-semibold text-primary-600 group-hover:text-primary-700"
                        >
                          Read →
                        </Link>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;